<template>
	<v-menu :attach="overlayTarget">
		<template v-slot:activator="{ props: menuProps }">
			<v-btn icon="add_business" variant="text" color="primary" density="comfortable" rounded="xl" class="overflow-hidden" v-bind="menuProps" @click="openAndFocus" />
		</template>
		<v-list density="compact">
			<v-list-item v-if="hasProspect" @click="openFor('PROSPECT')">{{ t('AddProspect') }}</v-list-item>
			<v-list-item v-if="hasADR" @click="openFor('ADR')">{{ t('AddADR') }}</v-list-item>
			<v-list-item v-if="hasNight" @click="openFor('NIGHT')">{{ t('AddNIGHT') }}</v-list-item>
		</v-list>
	</v-menu>

	<DialogControl scrollable v-model="isOpen" :attach="overlayTarget" class="stepper-form-dialog">
		<v-card>
			<v-card-title class="d-flex ma-0">
				<v-icon icon="store" color="primary"></v-icon>
				<span class="pa-2 text-primary">{{ dialogTitle }}</span>
				<v-spacer />
				<v-btn color="primary" icon="close" variant="text" @click="closeDialog" />
			</v-card-title>
			<v-card-text>
				<VStepperForm ref="stepperFormWrapper" v-model="formValues" :pages="pages" validate-on="blur" :validationSchema="validationSchema" @submit="save" class="v-stepper-form" error-icon="Exclamation" elevation="0" />
			</v-card-text>
			<v-card-actions v-if="isADR">
				<v-btn :disabled="!formIsValid" class="btn-bypass" block variant="tonal" density="default" color="secondary" @click="bypassSubmit" elevation="1">{{t('save')}}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>
</template>

<script setup>
// #region Imports
import { ref, computed, inject } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/composables/useTranslator';
import { VStepperForm } from '@wdns/vuetify-stepper-form';
import { object as yupObject, string as yupString, number as yupNumber } from 'yup';
import { log } from '@/utils/core/vueLogger.js';
import { setLegacyDeliveryLocations } from '@/api/sales/deliveryLocation';
import { getAttributes } from '@/api/core/attribute';
import { useStepperFormDialog } from '@/composables/useStepperFormDialog';
import { buildDynamicAttributeFields, serializeDynamicAttributeFields } from '@/utils/legacy/dynamicAttributeFields';
import DialogControl from '@/components/controls/DialogControl.vue';
// #endregion

const emit = defineEmits(['focus']);

// #region Refs & Vars
const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const iframeWin = iframeDataBridge.getIframeWindow();
const ecosVM = iframeDataBridge.getEcosVM();
const salesHelper = iframeDataBridge.getSalesHelper();
const toast = iframeWin.toast;

const { isOpen, stepperFormWrapper, openDialog, closeDialog, submit } = useStepperFormDialog('Delivery Location Form');
const deliveryLocationType = ref(null); // DLT chosen from the menu (PROSPECT/ADR/NIGHT)

const dialogTitleKeys = {
	PROSPECT: 'NewProspect',
	ADR: 'NewAdr',
	NIGHT: 'NewNight',
};
const dialogTitle = computed(() => t(dialogTitleKeys[deliveryLocationType.value?.reference] ?? 'NewProspect'));

// ADR/NIGHT only require companyName, addressLine, postcode, city; PROSPECT requires those plus the fields below
const ALWAYS_REQUIRED_FIELDS = ['companyName', 'addressLine', 'postcode', 'city'];
const PROSPECT_ONLY_REQUIRED_FIELDS = ['identificationNumber', 'country', 'accountId', 'distributionChannel', 'channelId'];
const isProspect = computed(() => deliveryLocationType.value?.reference === 'PROSPECT');
const isADR = computed(() => deliveryLocationType.value?.reference === 'ADR');

const formIsValid = computed(() => adrNightValidationSchema.isValidSync(formValues.value));

// Shared with the legacy forms (deliverylocation.js, pos.js, vwSalesCartForm.js) via
// public/m.eagle/app/data/countries.json — public/ isn't bundled by Vite, so fetch it
// instead of a static import.
const countries = ref([]);
fetch('/m.eagle/app/data/countries.json')
	.then((response) => response.json())
	.then((data) => {
		countries.value = data.map((c) => ({ id: c.id, label: t(c.labelKey) }));
	})
	.catch((error) => log('error', 'Delivery Location Form', `❌ Failed to load countries: ${error.message}`));

// Definitions for the dynamic "Attributes" step (see dynamicAttributeFields.js) — 'tokenSales'
// matches how deliveryLocationAttribute.js already fetches this same dataset.
const attributes = ref([]);
getAttributes(false, 'tokenSales')
	.then((data) => {
		attributes.value = Array.isArray(data) ? data : [];
	})
	.catch((error) => log('error', 'Delivery Location Form', `❌ Failed to load attributes: ${error.message}`));

// #region Fields (same rules as deliverylocationVM.prototype.checkInputAddress)

const formValues = ref({
	id: 0,
	companyName: null,
	deliveryLocationTypeId: null,
	identificationNumber: null,
	firstName: null,
	lastName: null,
	email: null,
	addressLine: null,
	postcode: null,
	country: null,
	phoneNumber: null,
	faxNumber: null,
	accountId: null,
	groupId: null,
	distributionChannel: null,
	city: null,
	channelId: null,
	POSTypeId: null,
	lat: null,
	lng: null,
});

function getDltOptions() {
	return ecosVM?.deliveryLocationTypes ?? [];
}

function getRestrictedAccounts() {
	const accounts = ecosVM?.accounts?.() ?? [];
	const restricted = salesHelper?.getRestrictedAccount?.() ?? [];
	return accounts.filter((a) => restricted.includes(a.id));
}

function getChannels() {
	return ecosVM?.channels ?? [];
}

function getPosTypes() {
	return ecosVM?.posTypes ?? [];
}

function getDistributionChannels() {
	return ecosVM?.distributionChannels ?? [];
}

function getDlGroups() {
	return ecosVM?.DLGroups ?? [];
}

function getExistingAddresses() {
	return (ecosVM?.deliveryLocations ?? []).map((dl) => normalizeForCompare([dl.country, dl.deliveryLocationTypeId, dl.accountId, dl.addressLine, dl.city, dl.postcode].join('/').toLocaleLowerCase()));
}

function isFieldRequired(name) {
	return ALWAYS_REQUIRED_FIELDS.includes(name) || (isProspect.value && PROSPECT_ONLY_REQUIRED_FIELDS.includes(name));
}

function buildPosFields() {
	return [
		{
			columns: {
				sm: 12,
				xl: 12,
			},
			label: t('RS'),
			name: 'companyName',
			required: isFieldRequired('companyName'),
			type: 'text',
			variant: 'outlined',
			'hide-details': 'auto',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('DeliveryLocationType'),
			name: 'deliveryLocationTypeId',
			readonly: true,
			type: 'select',
			items: getDltOptions(),
			'item-title': 'name',
			'item-value': 'id',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('IdentificationNumber'),
			name: 'identificationNumber',
			required: isFieldRequired('identificationNumber'),
			type: 'text',
			variant: 'outlined',
			counter: 14,
			maxlength: 14,
			'hide-details': 'auto',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('FirstName'),
			name: 'firstName',
			type: 'text',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('LastName'),
			name: 'lastName',
			type: 'text',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('Phone'),
			name: 'phoneNumber',
			type: 'text',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('Fax'),
			name: 'faxNumber',
			type: 'text',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 12,
				xl: 12,
			},
			label: t('Email'),
			name: 'email',
			type: 'text',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 12,
				xl: 12,
			},
			label: t('address'),
			name: 'addressLine',
			required: isFieldRequired('addressLine'),
			type: 'text',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('post code'),
			name: 'postcode',
			postCode: true,
			required: isFieldRequired('postcode'),
			type: 'text',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('city'),
			name: 'city',
			required: isFieldRequired('city'),
			type: 'text',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('Country'),
			name: 'country',
			required: isFieldRequired('country'),
			type: 'select',
			items: countries.value,
			'item-title': 'label',
			'item-value': 'id',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('Account'),
			name: 'accountId',
			required: isFieldRequired('accountId'),
			type: 'select',
			items: getRestrictedAccounts(),
			'item-title': 'name',
			'item-value': 'id',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('Group'),
			name: 'groupId',
			type: 'select',
			items: getDlGroups(),
			'item-title': 'name',
			'item-value': 'id',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('DistributionChannel'),
			name: 'distributionChannel',
			required: isFieldRequired('distributionChannel'),
			type: 'select',
			items: getDistributionChannels(),
			'item-title': 'label',
			'item-value': 'id',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('POSType'),
			name: 'POSTypeId',
			type: 'select',
			items: getPosTypes(),
			'item-title': 'label',
			'item-value': 'id',
			variant: 'outlined',
		},
		{
			columns: {
				sm: 6,
				xl: 6,
			},
			label: t('Channel'),
			name: 'channelId',
			required: isFieldRequired('channelId'),
			type: 'select',
			items: getChannels(),
			'item-title': 'name',
			'item-value': 'id',
			variant: 'outlined',
		},
	];
}

function buildPages() {
	const pages = [{ title: t('Address'), fields: buildPosFields() }];
	if (isProspect.value || isADR.value) {
		pages.push({ title: t('Attributes'), fields: buildDynamicAttributeFields({ attributes: attributes.value, t, entityType: 11, formValues }) });
	}
	return pages;
}

const pages = ref(buildPages());

function addressLineSchema() {
	return yupString()
		.required(isRequired(t('address')))
		.test('address-already-exists', t('AddressAlreadyExists'), (value) => {
			const concatenated = normalizeForCompare([formValues.value.country, formValues.value.deliveryLocationTypeId, formValues.value.accountId, value, formValues.value.city, formValues.value.postcode].join('/').toLocaleLowerCase());
			return !getExistingAddresses().includes(concatenated);
		});
}

const prospectValidationSchema = yupObject({
	companyName: yupString().required(isRequired(t('RS'))),
	identificationNumber: yupString()
		.required(isRequired(t('IdentificationNumber')))
		.matches(/^\d+$/, t('DigitOnly')),
	addressLine: addressLineSchema(),
	postcode: yupString()
		.required(isRequired(t('post code')))
		.min(4, t('PostCode4Digits')),
	city: yupString().required(isRequired(t('city'))),
	country: yupString().required(isRequired(t('Country'))),
	accountId: yupString().required(isRequired(t('Account'))),
	distributionChannel: yupString().required(isRequired(t('DistributionChannel'))),
	channelId: yupString().required(isRequired(t('Channel'))),
});

// ADR/NIGHT only require companyName, addressLine, postcode, city
const adrNightValidationSchema = yupObject({
	companyName: yupString().required(isRequired(t('RS'))),
	addressLine: addressLineSchema(),
	postcode: yupString()
		.required(isRequired(t('post code')))
		.min(4, t('PostCode4Digits')),
	city: yupString().required(isRequired(t('city'))),
});

const validationSchema = computed(() => (isProspect.value ? prospectValidationSchema : adrNightValidationSchema));

// #endregion

// #endregion

// #region Computed
// same guards as vwSFAPlanningVM's hasProspect/hasADR/hasNight, driving the add_business menu
const restrictedDLTReferences = computed(() => (salesHelper?.getRestrictedDeliveryLocationTypes() ?? []).map((d) => d.reference));
const hasProspect = computed(() => restrictedDLTReferences.value.includes('PROSPECT') && !!salesHelper?.hasPermission(0, 'ManageProspect', iframeWin?.permissionMask?.CREATE ?? 4));
const hasADR = computed(() => restrictedDLTReferences.value.includes('ADR'));
const hasNight = computed(() => restrictedDLTReferences.value.includes('NIGHT'));

// const dltLocked = computed(() => !!deliveryLocationType.value?.id && !isProspect.value);
// #endregion

// #region Methods

function isRequired(field) {
	return t('RequiredField') + ': ' + `${field}`;
}

function normalizeForCompare(value) {
	return String(value ?? '')
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.replace(/\s/g, '');
}

function openAndFocus(event) {
	emit('focus', event);
}

function openFor(reference) {
	deliveryLocationType.value = getDltOptions().find((d) => d.reference === reference) ?? null;
	if (!deliveryLocationType.value) {
		log('warn', 'Delivery Location Form', `⚠️ No delivery location type found for reference: ${reference}`);
		return;
	}
	pages.value = buildPages();
	formValues.value.deliveryLocationTypeId = deliveryLocationType.value.id;
	openDialog();
}

async function bypassSubmit() {
	await save();
}

async function save() {
	await submit(async () => {
		try {
			const context = iframeDataBridge.getContext();
			if (!context?.isOnline?.()) {
				toast(t('OfflineOnlyAction'), 'warning');
				return;
			}

			const payload = { ...formValues.value };
			payload.reference = payload.companyName;
			payload.label = payload.companyName;
			payload.groupId = payload.groupId === null ? 0 : payload.groupId;
			payload.POSTypeId = payload.POSTypeId === null ? 0 : payload.POSTypeId;

			if (context?.user?.organisationId) payload.organisations = [context.user.organisationId];

			const result = await new Promise((resolve, reject) => {
				salesHelper.newDeliveryLocation(payload, (oldId, newDl) => {
					if (!newDl) reject(new Error('Api did not send back a delivery location'));
					else resolve(newDl);
				});
			});

			['availability_Mon', 'availability_Tue', 'availability_Wed', 'availability_Thu', 'availability_Fri', 'availability_Sat', 'availability_Sun', 'deliveryDays'].forEach((key) => {
				if (result[key] == null) result[key] = '';
			});

			const liveEcosVM = iframeWin?.externis?.ecosVM;

			if (liveEcosVM?.currentViewModel?.deliveryLocations) {
				liveEcosVM.currentViewModel.deliveryLocations.push(result);
			}

			if (liveEcosVM) {
				liveEcosVM.deliveryLocations.push(result);
			}

			salesHelper.saveDlAttributes([{ id: result.id, attributes: serializeDynamicAttributeFields(pages.value[1].fields, payload) }], () => {});
			await geocodeAndSaveGeo(result);

			result._isNewAddress = true;
			const sender = liveEcosVM?.currentViewModel;
			sender?.__modalDLSelected?.(result, sender);

			// pass new result to other component using iframedatabridge
			setLegacyDeliveryLocations(liveEcosVM?.deliveryLocations);
			iframeDataBridge.getIframeWindow().amplify.publish('DeliveryLocationCreated', { result });
			closeDialog();
		} catch (error) {
			log('error', 'Delivery Location Form', `❌ Adding Delivery Location Failed: ${error.message}`);
			closeDialog();
		}
	});
}

async function geocodeAndSaveGeo(result) {
	const mapHelper = iframeWin?.mapHelper;
	const storage = iframeDataBridge.getStorage();
	if (!mapHelper || !storage) return;

	const address = `${result.addressLine} - ${result.postcode} - ${result.city} - ${result.country}`;

	await new Promise((resolve) => {
		mapHelper.init(() => {
			mapHelper.codeAddress(address, (results, status) => {
				if (status !== 'OK') {
					log('warn', 'Delivery Location Form', `Geocode failed: ${status}`);
					return resolve();
				}
				const geo = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() };
				storage.get('DELIVERYLOCATIONGEO', (geos) => {
					const updated = [...(geos ?? []).filter((g) => g.deliveryLocationId !== result.id), { deliveryLocationId: result.id, latitude: geo.lat, longitude: geo.lng, modifiedDate: new Date(), localOnly: true }];
					storage.save('DELIVERYLOCATIONGEO', updated, false, resolve);
				});
			});
		});
	});
}

// #endregion
</script>
